<template>
  <div id="page-campaign-statistics">
    <vs-alert v-if="!activeCampaign" active="true">
      {{ $t("Dashboard.NoActiveCampaign") }}
    </vs-alert>
    <vx-title-bar :route="$route" :isRTL="$vs.rtl" v-if="activeCampaign">
      <campaign-drop-down />
      <strategy-drop-down v-show="activeCampaign.hasStrategies" />
      <vc-date-picker
        color="purple"
        mode="range"
        :columns="2"
        :max-date="new Date()"
        v-model="localRange"
      />
      <vs-dropdown
        vs-trigger-click
        v-if="$acl.check('editor')"
        class="ml-auto md:block hidden cursor-pointer"
      >
        <vs-button radius icon="icon-more-vertical" icon-pack="feather" />
        <vs-dropdown-menu>
          <vs-dropdown-item>
            <div @click="gotoDetails" class="flex items-center">
              <feather-icon
                icon="ArrowRightCircleIcon"
                class="inline-block mr-2"
                svgClasses="w-4 h-4"
              />
              <span>{{ this.$t("Campaign.View") }}</span>
            </div>
          </vs-dropdown-item>
        </vs-dropdown-menu>
      </vs-dropdown>
    </vx-title-bar>

    <div class="vx-row" v-if="activeCampaign">
      <div class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/5 xl:w-1/5 mb-base">
        <statistics-card-line
          v-if="impressionsLastWeek"
          icon="UsersIcon"
          :statistic="totalImpressions | k_formatter"
          :statisticTitle="$t('Analytics.Impressions')"
          :chartData="impressionsLastWeek"
          type="area"
          color="danger"
          xaxisType="datetime"
        />
      </div>

      <div class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/5 xl:w-1/5 mb-base">
        <statistics-card-line
          v-if="clicksLastWeek"
          icon="MousePointerIcon"
          :statistic="totalClicks | k_formatter"
          :statisticTitle="$t('Analytics.Clicks')"
          :chartData="clicksLastWeek"
          type="area"
          xaxisType="datetime"
        />
      </div>

      <div class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/5 xl:w-1/5 mb-base">
        <statistics-card-line
          v-if="conversionsLastWeek"
          icon="ShoppingCartIcon"
          :statistic="totalConversions | k_formatter"
          :statisticTitle="$t('Analytics.Conversions')"
          :chartData="conversionsLastWeek"
          color="success"
          type="area"
          xaxisType="datetime"
        />
      </div>

      <div class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/5 xl:w-1/5 mb-base">
        <statistics-card-line
          v-if="costsLastWeek"
          icon="DollarSignIcon"
          :statistic="totalCosts | mk_formatter"
          :statisticTitle="$t('Analytics.AmountSpent')"
          :chartData="costsLastWeek"
          color="danger"
          type="area"
          xaxisType="datetime"
        />
      </div>

      <div class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/5 xl:w-1/5 mb-base">
        <statistics-card-line
          v-if="avgCpcLastWeek"
          icon="ShoppingBagIcon"
          :statistic="avgCpc | mk_formatter"
          :statisticTitle="$t('Analytics.CostsPerClick')"
          :chartData="avgCpcLastWeek"
          color="warning"
          type="area"
          xaxisType="datetime"
        />
      </div>
    </div>
    <div class="vx-row" v-if="activeCampaign">
      <div class="vx-col w-full mb-base">
        <div class="vx-card">
          <campaign-statistics-line-chart />
        </div>
      </div>
    </div>
    <div class="vx-row" v-if="activeCampaign">
      <!-- LINE CHART -->
      <div class="vx-col w-full mb-base">
        <month-comparison />
      </div>
    </div>
  </div>
</template>


<script>
import { mapGetters } from 'vuex'
import MonthComparison from "@/views/components/MonthComparison"
import CampaignStatisticsLineChart from "../charts/CampaignStatisticsLineChart"
import StatisticsCardLine from "@/components/statistics-cards/StatisticsCardLine.vue"
import CampaignDropDown from "@/views/components/CampaignDropDown.vue"
import StrategyDropDown from "@/views/components/StrategyDropDown.vue"

let _this = {}

export default {
  data () {
    return {
      localRange: {},
      settings: { 
        maxScrollbarLength: 60,
        wheelSpeed: .60
      }
    }
  },
  components: {
    MonthComparison,
    CampaignStatisticsLineChart,
    StatisticsCardLine,
    CampaignDropDown,
    StrategyDropDown
  },
  created () {
    _this = this
    this.localRange = this.$store.state.statistics.range
    this.refreshData()
  },
  computed: {
    ...mapGetters([
      "activeOrganisation",
      "activeCampaign",
      "activeStrategy"
    ]),
    scrollbarTag () { return this.$store.getters.scrollbarTag },
    totalClicks () {
      const stats = this.$store.state.statistics.dashboard.statistics
      const sum = stats.reduce((a, b) => a + b.clicks, 0)
      return sum
    },
    totalConversions () {
      const stats = this.$store.state.statistics.dashboard.statistics
      const sum = stats.reduce((a, b) => a + b.totalConversions, 0)
      return sum
    },
    clicksLastWeek () {
      return [
        {
          name: this.$t("Analytics.Clicks"),
          data: this.last7days.map(a => [a.date, a.clicks])
        }
      ]
    },
    conversionsLastWeek () {
      return [
        {
          name: this.$t("Analytics.Conversions"),
          data: this.last7days.map(a => [a.date, a.totalConversions])
        }
      ]
    },
    last7days () {
      const stats = this.$store.state.statistics.dashboard.statistics.map(a => a)
      const sorted = stats.sort((a, b) => {
        return (b.date < a.date) ? -1 : ((b.date > a.date) ? 1 : 0)
      })
      const max = sorted.length < 7 ? sorted.length : 7
      const ret = []
      for (let index = 0; index < max; index++) {
        ret.push(sorted[index])
      }
      return ret
    },
    impressionsLastWeek () {
      return [
        {
          name: this.$t("Analytics.Impressions"),
          data: this.last7days.map(a => [a.date, a.impressions])
        }
      ]
    },
    totalImpressions: function () {
      const stats = this.$store.state.statistics.dashboard.statistics
      const sum = stats.reduce((a, b) => a + b.impressions, 0)
      return sum
    },
    totalCosts: function () {
      const stats = this.$store.state.statistics.dashboard.statistics
      const sum = stats.reduce((a, b) => a + b.revenue, 0)
      return sum
    },
    costsLastWeek () {
      const ret = [
        {
          name: "Costs",
          data: this.last7days.map(a => [a.date, a.revenue.toFixed(2)])
        }
      ]
      return ret
    },
    activeCampaignId () {
      return this.$store.state.AppActiveUser.activeCampaignId
    },
    activeStrategyId () {
      return this.$store.state.AppActiveUser.activeStrategyId
    },
    avgCpcLastWeek () {
      return [
        {
          name: "Costs per click",
          data: this.last7days.map(a => {
            if (a.clicks === 0) {
              return [a.date, 0]
            }
            return [a.date, (a.revenue / a.clicks).toFixed(2)]
          })
        }
      ]
    },
    avgCpc () {
      if (this.totalClicks === 0) {
        return 0
      }
      return this.totalCosts / this.totalClicks
    }
  },
  mounted () {
    // const scroll_el = this.$refs.chatLogPS.$el || this.$refs.chatLogPS
    // scroll_el.scrollTop = this.$refs.chatLog.scrollHeight
  },
  methods: {
    customizeCtr (cellInfo) {
      if (!cellInfo.valueText) {
        return ""
      }
      return `${cellInfo.valueText}%`
    },
    refreshData () {
      const activeStrategy = this.activeStrategy
      const payload = {
        idCampaign: this.activeCampaign ? this.$store.state.AppActiveUser.activeCampaignId : null,
        startDate: this.localRange.start,
        endDate: this.localRange.end,
        retailBranchId: activeStrategy ? activeStrategy.retailBranchId : null
      }
      _this.$store.dispatch("statistics/fetchStatisticsDashboard", payload)
    },
    gotoDetails () {
      this.$router.push(`/campaign/view/${this.activeCampaign.id}`)
    }
  },
  watch: {
    localRange: function (val) {
      this.$store.dispatch("statistics/setRange", val)
      this.refreshData()
    },
    activeCampaignId: function () {
      this.refreshData()
    },
    activeStrategyId: function () {
      this.refreshData()
    },
  },
}
</script>
